import { useMemo } from "react";
import { Box, Skeleton, Text, type Omittable } from "@salestrak/ui";

import {
  ActivityProvider,
  type ActivityContextValue,
} from "#app/components/issue-view/activity/activity-context";
import { ActivityItem } from "#app/components/issue-view/activity/activity-item";
import { NoteComposer } from "#app/components/issue-view/activity/note-composer";
import { NoteItem } from "#app/components/issue-view/activity/note-item";
import { NoteThread } from "#app/components/issue-view/activity/note-thread";
import { useDockedComposer } from "#app/components/issue-view/activity/docked-composer-context";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";

function noteTimestamp(note: NoteNode): number {
  return new Date(note.lastUpdateDate ?? note.creationDate).getTime();
}

/**
 * The Comments module's body, shared by the Project, Account and Contact
 * summaries: the latest N notes (threads and replies alike), newest first,
 * with the composer under them. A visit whose note rides a combined age
 * activity renders as that `ActivityItem` so the chips it carries come with
 * it. The host resolves `activity`/`notes` once — the issue summary from its
 * live queries, the entity providers through the local activity adapter — so
 * this body stays presentation-only.
 */
export function CommentsWidgetBody({
  entityId,
  activity,
  activityLoading,
  notes,
  limit,
}: Omittable<{
  entityId?: string;
  activity?: ActivityContextValue;
  activityLoading?: boolean;
  notes?: NoteNode[];
  limit: number;
}>) {
  const dock = useDockedComposer();
  const rows = useMemo(() => {
    const notesById = new Map<string, NoteNode>();
    const ageActivityByNoteId = new Map(
      (activity?.activities ?? []).flatMap((entry) => {
        const noteId = entry.changeset?.age?.after?.noteId;
        return noteId ? [[noteId, entry] as const] : [];
      }),
    );

    for (const note of notes ?? []) {
      notesById.set(note.noteId, note);
      for (const reply of note.replies ?? []) {
        notesById.set(reply.noteId, reply);
      }
    }

    return Array.from(notesById.values())
      .sort((left, right) => noteTimestamp(right) - noteTimestamp(left))
      .slice(0, limit)
      .map((note) => ({ note, ageActivity: ageActivityByNoteId.get(note.noteId) }));
  }, [activity?.activities, limit, notes]);

  if (activityLoading && rows.length === 0) {
    return (
      <Box direction="column" gap="sm">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-18 w-full" />
        ))}
      </Box>
    );
  }

  if (!entityId || !activity) {
    return (
      <Box paddingY="sm">
        <Text size="sm" variant="muted">No comments yet</Text>
      </Box>
    );
  }

  return (
    <ActivityProvider value={activity}>
      <Box border rounded="sm" overflow="hidden" padding="sm" gap="sm">
        {rows.length > 0 ? (
          rows.map(({ note, ageActivity }, index) =>
            ageActivity ? (
              <ActivityItem
                key={`${note.noteId}-${index}`}
                issueId={entityId}
                activity={ageActivity}
                last={index === rows.length - 1}
              />
            ) : note.parentNoteId ? (
              <NoteItem
                key={`${note.noteId}-${index}`}
                issueId={entityId}
                noteId={note.noteId}
              />
            ) : (
              <NoteThread
                key={`${note.noteId}-${index}`}
                issueId={entityId}
                noteId={note.noteId}
              />
            ),
          )
        ) : (
          <Box paddingY="sm">
            <Text size="sm" variant="muted">No comments yet</Text>
          </Box>
        )}
        {dock ? (
          // In the phone sheet the comment is written in the bar on the
          // keyboard; the widget's field is the door that calls it up.
          <Box
            as="button"
            type="button"
            onClick={dock.compose}
            border
            rounded="lg"
            background="card"
            height={38}
            align="center"
            paddingX="md"
            cursor="pointer"
            group="box"
          >
            <Text size="sm" variant="muted">Write a comment</Text>
          </Box>
        ) : (
          <NoteComposer issueId={entityId} />
        )}
      </Box>
    </ActivityProvider>
  );
}
